import React from "react";
import { useSelector } from "react-redux";
import { HtmlTooltip, IconContainer, StyledMainBox, StyledStack } from "../../../../components/commonComponents/StyledComponents/styled";
import { Box, Typography } from "@mui/material";
import { Archive, Delete, Edit, Error, Unarchive } from "@mui/icons-material";
import NewWorkspaceSection from "./NewWorkspaceSection";
import ListingDataTableForModal from "../../../../components/commonComponents/TabularLists/ListingDataTableForModal";
import { archiveWorkspaceSection, deleteWorkspaceSection, formatImpressionCompanyLabel, getTenantCompanyId, recoverWorkspaceSection } from "../../../../api_calls/WorkspaceSections";
import Confirmation from "../../../../components/commonComponents/ConfirmationBox/Confirmation";
import { toast } from "react-toastify";

const MISSING_IMPRESSION_WARNING = "No impressions section configured for this company. Automated publishing of impressions reports disabled for campaigns created by users of this retailer.";

const formatSectionFilter = (filter) => {
    const type = filter?.type ?? "";
    const typeLabel = type ? type.charAt(0).toUpperCase() + type.slice(1) : "";

    return typeLabel ? `${filter.keyword} (${typeLabel})` : filter.keyword;
};

const SectionsList = ({ listType, sectionType }) => {
    const currentStateData = useSelector((state) => state.userSelections);
    const [editSectionPopup, setEditSectionPopup] = React.useState(false);
    const [sectionPopupMode, setSectionPopupMode] = React.useState("edit");
    const [sectionDetailToEdit, setSectionDetailToEdit] = React.useState({});
    const [sectionCompanyContext, setSectionCompanyContext] = React.useState(null);

    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [sectionIdToArchive, setSectionIdToArchive] = React.useState("");

    const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
    const [sectionIdToDelete, setSectionIdToDelete] = React.useState("");
    const archivedList = listType === "archived" ? true : false;
    const impressionList = sectionType === "impression";

    // Performance: filter available vs archived from workspace_performance_sections.
    // Impressions: already normalized in Redux (including missing placeholders).
    const listItems = React.useMemo(() => {
        if (impressionList === true) {
            return currentStateData.workspace_impression_sections ?? [];
        }

        const performanceSections = currentStateData.workspace_performance_sections ?? [];
        return performanceSections.filter((sec) => sec.archived === archivedList);
    }, [
        currentStateData.workspace_impression_sections,
        currentStateData.workspace_performance_sections,
        archivedList,
        impressionList,
    ]);

    const openSectionModal = (section) => {
        if (section.__missingImpressionSection === true) {
            setSectionPopupMode("add");
            setSectionDetailToEdit({});
            setSectionCompanyContext({
                id: section.__retailerCompanyId,
                name: section.__retailerCompanyName,
            });
        } else {
            setSectionPopupMode("edit");
            setSectionDetailToEdit(section);
            setSectionCompanyContext(null);
        }

        setEditSectionPopup(true);
    };

    const tenantCompanyId = getTenantCompanyId();

    const columns = React.useMemo(() => {
        const companyColumn = {
            id: "company",
            header: "Company",
            cellClassName: "text-wrapper wrapLargeText",
            render: (section) => formatImpressionCompanyLabel(
                section.company_name,
                section.__retailerCompanyId,
                tenantCompanyId
            ),
        };

        const remainingColumns = [
            {
                id: "section_name",
                header: (
                    <>
                        Section Name
                        <Typography
                            component="span"
                            variant="caption"
                            display="block"
                            sx={{ fontWeight: "inherit", whiteSpace: "nowrap" }}
                        >
                            (in CAP Explorer)
                        </Typography>
                    </>
                ),
                headerSx: { minWidth: "170px", width: "170px" },
                cellSx: { minWidth: "170px", width: "170px" },
                cellClassName: "text-wrapper wrapLargeText",
                render: (section) => section.__missingImpressionSection === true ? "-" : section.name,
            },
            {
                id: "embeddable_id",
                header: "Embeddable ID",
                headerSx: { width: "180px" },
                cellSx: { width: "180px" },
                render: (section) => section.__missingImpressionSection === true ? "-" : section.id_embeddable,
            },
            {
                id: "filters",
                header: "Filters",
                cellClassName: "text-wrapper",
                render: (section) => {
                    if (section.__missingImpressionSection === true) {
                        return "-";
                    }

                    return section.filters?.length > 0
                        ? section.filters.map(formatSectionFilter).join(", ")
                        : "No filters applied";
                },
            },
            {
                id: "description",
                header: "Description",
                headerSx: { width: "140px" },
                cellSx: { width: "140px" },
                render: (section) => section.__missingImpressionSection === true ? "-" : section.description,
            },
            {
                id: "action",
                header: "Action",
                render: (section) => (
                    <StyledStack direction="row" spacing={1}>
                        {/* Available sections keep the existing edit/archive behavior. */}
                        {
                            archivedList === false && (
                                <>
                                    <Box sx={{ position: "relative", display: "inline-block" }}>
                                        <HtmlTooltip title="Edit">
                                            <IconContainer>
                                                <a
                                                    href="#editClip"
                                                    aria-label={"edit workspace section for " + (section.name ?? section.company_name)}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        openSectionModal(section);
                                                    }}
                                                >
                                                    <Edit fontSize="small" sx={{ color: "#AF3241", marginTop: "6px" }} />
                                                </a>
                                            </IconContainer>
                                        </HtmlTooltip>
                                        {
                                            section.__missingImpressionSection === true && (
                                                <HtmlTooltip title={
                                                    <Box width={250}>
                                                        <StyledMainBox>
                                                            <Typography variant="body2">
                                                                {MISSING_IMPRESSION_WARNING}
                                                            </Typography>
                                                        </StyledMainBox>
                                                    </Box>}
                                                >
                                                    <Error
                                                    fontSize="small"
                                                        sx={{
                                                            position: "absolute",
                                                            right: -5,
                                                            bottom: 5,
                                                            color: "#AF3241",
                                                            backgroundColor: "#fff",
                                                            borderRadius: "50%",
                                                        }}
                                                    />
                                                </HtmlTooltip>
                                            )
                                        }
                                    </Box>
                                    {
                                        sectionType !== "impression" && (
                                            <HtmlTooltip title="Archive">
                                                <IconContainer>
                                                    <a
                                                        href="#editClip"
                                                        aria-label={"archive workspace section for " + section.name}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setSectionIdToArchive(section.id);
                                                            setShowConfirmation(true);
                                                        }}
                                                    >
                                                        <Archive fontSize="small" sx={{ color: "#AF3241", marginTop: "6px" }} />
                                                    </a>
                                                </IconContainer>
                                            </HtmlTooltip>
                                        )
                                    }
                                </>
                            )
                        }
                        {/* Archived sections keep recover/delete actions. */}
                        {
                            archivedList === true && (
                                <>
                                    <HtmlTooltip title="Recover">
                                        <IconContainer>
                                            <a
                                                href="#recoverSection"
                                                aria-label={"recover workspace section for " + section.name}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    recoverWorkspaceSection(section.id).then((res) => {
                                                        if (res.success === false) {
                                                            toast("Sorry, something went wrong.", { type: "error" });
                                                        }
                                                    });
                                                }}
                                            >
                                                <Unarchive fontSize="small" sx={{ color: "#AF3241", marginTop: "6px" }} />
                                            </a>
                                        </IconContainer>
                                    </HtmlTooltip>
                                    <HtmlTooltip title="Delete">
                                        <IconContainer>
                                            <a
                                                href="#deleteSection"
                                                aria-label={"Delete workspace section for " + section.name}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setSectionIdToDelete(section.id);
                                                    setShowDeleteConfirmation(true);
                                                }}
                                            >
                                                <Delete fontSize="small" sx={{ color: "#AF3241", marginTop: "6px" }} />
                                            </a>
                                        </IconContainer>
                                    </HtmlTooltip>
                                </>
                            )
                        }
                    </StyledStack>
                ),
            },
        ];

        return impressionList === true ? [companyColumn, ...remainingColumns] : remainingColumns;
    }, [archivedList, impressionList, sectionType, tenantCompanyId]);

    return (
        <>
            <ListingDataTableForModal
                columns={columns}
                rows={listItems}
                rowKey={(section) => "section_id_" + section.id}
                emptyText={`No ${archivedList === true ? "archived" : "available"} sections found.`}
                pagination={null}
                onPageChange={null}
                onPageSizeChange={null}
                tableMinWidth={700}
            />
            {/* ------- confirmation popup (on click of archive button) -------- */}
            {showConfirmation && (
                <Confirmation
                    showConfirmationPopup={showConfirmation}
                    closePopup={(e) => {
                        setSectionIdToArchive("");
                        setShowConfirmation(false);
                    }}
                    confirmationActionCall={() => {
                        archiveWorkspaceSection(sectionIdToArchive).then((res) => {
                            if (res.success === true) {
                                setSectionIdToArchive("");
                                setShowConfirmation(false);
                            } else {
                                toast("Sorry, something went wrong.", { type: "error" });
                            }
                        });
                    }}
                    confirmationMessage={
                        <>
                            You are archiving a dashboard section. You can recover archived sections any time. <Box height={10} /> Do you want to proceed?
                        </>
                    }
                />
            )}




            {/* --------------- delete confirmation --------------- */}
            {showDeleteConfirmation && (
                <Confirmation
                    showConfirmationPopup={showDeleteConfirmation}
                    closePopup={(e) => {
                        setSectionIdToDelete("");
                        setShowDeleteConfirmation(false);
                    }}
                    confirmationActionCall={() => {
                        deleteWorkspaceSection(sectionIdToDelete).then((res) => {
                            if (res.success === true) {
                                setSectionIdToDelete("");
                                setShowDeleteConfirmation(false);
                            } else {
                                toast("Sorry, something went wrong.", { type: "error" });
                            }
                        });
                    }}
                    confirmationMessage={
                        <>
                            You are deleting an archived dashboard section. If the section is included in any published report, it will be removed from the report. This action cannot be undone. <Box height={10} /> Do you want to proceed?
                        </>
                    }
                />
            )}
            {/* --------- new section popup ----------- */}
            {
                editSectionPopup === true && (
                    <NewWorkspaceSection
                        showModal={editSectionPopup}
                        sectionDetails={sectionDetailToEdit}
                        sectionType={sectionType}
                        usedFor={sectionPopupMode}
                        sectionCompany={sectionCompanyContext}
                        closeModal={() => {
                            setEditSectionPopup(false);
                            setSectionPopupMode("edit");
                            setSectionCompanyContext(null);
                        }}
                    />
                )
            }

        </>
    );
};

export default SectionsList;
